// Wedding API Response Adapters
// Maps wedding API responses to Aurora's User interface

import { LoginResponse, JoinEventResponse } from './auth';
import { getAvatarUrl } from 'utils/avatars';

/**
 * Adapt couple login response to Aurora User format
 *
 * Design:
 * - id: Primary identifier (used by Aurora components)
 * - coupleId: Same as id, kept for semantic clarity in authorization
 * - eventId: The event this couple owns
 */
export function adaptCoupleToUser(apiResponse: LoginResponse): import('types/users').User {
  const { couple } = apiResponse;

  return {
    id: couple.id,
    name: couple.displayName || couple.email.split('@')[0],
    email: couple.email,
    avatar: getAvatarUrl(couple.id, { name: couple.displayName }),
    status: 'active',
    role: 'couple',
    coupleId: couple.id,
    eventId: couple.eventId,
    joinCode: apiResponse.joinCode,
    displayName: couple.displayName,
    uploaderId: 'couple', // Match backend's hardcoded couple uploaderId
    isEmailVerified: couple.isEmailVerified ?? false,
  };
}

/**
 * Adapt guest join response to Aurora User format
 *
 * Design:
 * - id: Primary identifier (uploaderId like "guest-001")
 * - uploaderId: Same as id, kept for semantic clarity
 * - eventId: The event this guest joined
 */
export function adaptGuestToUser(apiResponse: JoinEventResponse): import('types/users').User {
  return {
    id: apiResponse.uploaderId,
    name: apiResponse.displayName,
    email: '', // Guests don't have email
    avatar: getAvatarUrl(apiResponse.uploaderId, { name: apiResponse.displayName }),
    status: 'active',
    role: 'uploader',
    eventId: apiResponse.eventId,
    uploaderId: apiResponse.uploaderId, // Same as id, but semantically clear
    displayName: apiResponse.displayName,
    // Plan tier of the couple's event — drives nav gating (e.g. hiding
    // Wishes/Quiz on non-Heirloom plans) so guests never see dead links.
    plan: apiResponse.plan,
    // Event type on the guest session so event-type-aware copy resolves on
    // guest surfaces (e.g. the upload section subtitle + setup-limit toasts,
    // which read `sessionUser.eventType`). Without this, guests fall back to
    // the "wedding" base copy on a baptism. Mirrors the couple session.
    eventType: apiResponse.eventType,
  };
}
